import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import apiService from '../../services/apiService';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;

const Page = styled.div`
  min-height: 100vh; background: #0a0a0f; color: #e2e8f0; font-family: 'Inter', sans-serif; padding: 20px;
`;
const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px; margin-bottom: 28px; padding-bottom: 20px; border-bottom: 1px solid #1e2030;
`;
const BackBtn = styled.button`
  background: none; border: none; color: #64748b; cursor: pointer; padding: 8px; border-radius: 8px;
  &:hover { background: #1e2030; color: #e2e8f0; }
`;
const TitleArea = styled.div`flex: 1;`;
const PageTitle = styled.h1`
  font-size: 22px; font-weight: 800; margin: 0;
  background: linear-gradient(135deg, #f43f5e, #fb923c); -webkit-background-clip: text; -webkit-text-fill-color: transparent;
`;
const NavTabs = styled.div`display: flex; gap: 8px;`;
const NavTab = styled.button`
  background: ${p => p.active ? 'rgba(244,63,94,0.15)' : 'transparent'};
  border: 1px solid ${p => p.active ? '#f43f5e' : '#1e2030'};
  color: ${p => p.active ? '#f43f5e' : '#64748b'};
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer;
  &:hover { border-color: #f43f5e; color: #f43f5e; }
`;

const Table = styled.div`background: #111827; border: 1px solid #1e2030; border-radius: 14px; overflow-x: auto; animation: ${fadeIn} 0.5s ease;`;
const TH = styled.div`
  display: grid; grid-template-columns: 1fr 100px 100px 100px 100px; gap: 8px;
  padding: 12px 16px; background: #0d1117; font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;
`;
const TR = styled.div`
  display: grid; grid-template-columns: 1fr 100px 100px 100px 100px; gap: 8px;
  padding: 16px; border-bottom: 1px solid #1e2030; font-size: 13px; font-weight: 600;
  &:hover { background: #1a1f2e; }
  &:last-child { border-bottom: none; }
`;

function VwapPositionsPage() {
  const navigate = useNavigate();
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const { data } = await apiService.axiosInstance.get('/api/vwap/positions');
      if (data.success) setPositions(data.positions);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPositions(); }, []);

  const fmt = n => n != null ? Number(n).toLocaleString() : '0';

  return (
    <Page>
      <TopBar>
        <BackBtn onClick={() => navigate('/labs')}><ArrowLeft size={18} /></BackBtn>
        <TitleArea>
          <PageTitle>실시간 포지션 (Positions)</PageTitle>
        </TitleArea>
        <NavTabs>
          <NavTab onClick={() => navigate('/labs/vwap')}>대시보드</NavTab>
          <NavTab active>포지션</NavTab>
          <NavTab onClick={() => navigate('/labs/vwap/history')}>매매내역</NavTab>
        </NavTabs>
      </TopBar>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={fetchPositions} disabled={loading} style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer'
        }}>
          <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
          새로고침
        </button>
      </div>

      <Table>
        <TH><span>종목</span><span>매수가</span><span>수량</span><span>현재 투자금액</span><span>당일 고점(TP)</span></TH>
        {positions.map(p => (
          <TR key={p.id}>
            <div>
              <div style={{ color: '#fff' }}>{p.name}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{p.code}</div>
            </div>
            <span style={{ color: '#fb923c' }}>₩{fmt(p.buyPrice)}</span>
            <span>{fmt(p.shares)}주</span>
            <span>₩{fmt(p.buyPrice * p.shares)}</span>
            <span style={{ color: '#f43f5e' }}>₩{fmt(p.morningHigh)}</span>
          </TR>
        ))}
        {positions.length === 0 && !loading && (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>보유중인 포지션이 없습니다.</div>
        )}
      </Table>
    </Page>
  );
}

export default VwapPositionsPage;
